import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styles from "./Account.module.css";
import { countriesData } from "../constant/countries";
import { signUp } from "../redux/actions/action-creator";
import { clearGuestSession, performGuestLogin } from "../utils/guestLogin";
import { selectConfigurationData } from "../redux/selectors/configurationSelectors";
import { getScreenConfig } from "../utils/uiConfig";
import {
  getDefaultAuthPhoneCountry,
  isIndiaPhoneCountry,
} from "../utils/authPhoneCountry";

const initialFormState = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  dob: "",
  nationality_id: "",
  password: "",
  confirm_password: "",
  referral_code: "",
  terms: false,
};

const getDisplayValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== "") || "";

const getFirstObjectValue = (item, keys = []) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  return getDisplayValue(...keys.map((key) => item?.[key]));
};

const getFallbackObjectValue = (item, preferredPatterns = [], blockedPatterns = []) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  const entries = Object.entries(item).filter(([, value]) => getDisplayValue(value));
  const matchesPattern = (key, patterns) =>
    patterns.some((pattern) => key.toLowerCase().includes(pattern));

  const matchedEntry = entries.find(([key]) => {
    const normalizedKey = key.toLowerCase();
    return (
      matchesPattern(normalizedKey, preferredPatterns) &&
      !matchesPattern(normalizedKey, blockedPatterns)
    );
  });

  const fallbackEntry =
    matchedEntry ||
    entries.find(([key]) => !matchesPattern(key.toLowerCase(), blockedPatterns));

  return fallbackEntry?.[1] || "";
};

const getNationalityLabel = (value) => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object" && value !== null) {
    return (
      getFirstObjectValue(value, [
        "nationality_name",
        "nationalityName",
        "nationality_title",
        "nationalityTitle",
        "country_nationality",
        "countryNationality",
        "nationality",
        "name",
        "label",
        "title",
        "text",
        "country_name",
        "countryName",
        "value",
      ]) ||
      getFallbackObjectValue(value, ["nationality", "name", "label", "title", "text"], [
        "id",
        "code",
        "status",
      ])
    );
  }

  if (value || value === 0) {
    return String(value);
  }

  return "";
};

const getArrayCandidates = (source) => {
  if (Array.isArray(source)) {
    return source.reduce(
      (arrays, item) => arrays.concat(getArrayCandidates(item)),
      []
    ).concat([source]);
  }

  if (!source || typeof source !== "object") {
    return [];
  }

  return [
    source?.nationalities,
    source?.nationality_list,
    source?.nationalityList,
    source?.nationalityOptions,
    source?.nationality_options,
    source?.data?.nationalities,
    source?.data?.nationality_list,
    source?.data?.nationalityList,
    source?.data?.nationalityOptions,
    source?.data?.nationality_options,
    source?.response_data?.nationalities,
    source?.response_data?.nationality_list,
    source?.response_data?.nationalityList,
    source?.response_data?.nationalityOptions,
    source?.response_data?.nationality_options,
  ];
};

const normalizeNationalityOptions = (...sources) => {
  const candidateArrays = sources.reduce(
    (arrays, source) => arrays.concat(getArrayCandidates(source)),
    []
  );
  const directNationalityList = sources.find(Array.isArray);
  const directNationalityObject = sources.find(
    (source) => source && typeof source === "object" && !Array.isArray(source)
  );
  const nationalityList =
    directNationalityList ||
    candidateArrays.find(Array.isArray) ||
    (directNationalityObject
      ? Object.entries(directNationalityObject).map(([key, value]) =>
          value && typeof value === "object" ? { key, ...value } : { key, value }
        )
      : []);

  return nationalityList
    .map((item, index) => {
      const objectEntries =
        item && typeof item === "object"
          ? Object.entries(item).filter(([, value]) => getDisplayValue(value))
          : [];
      const singleEntry = objectEntries.length === 1 ? objectEntries[0] : null;
      const label = getNationalityLabel(item) || singleEntry?.[1] || "";
      const id =
        getFirstObjectValue(item, [
          "nationality_id",
          "nationalityId",
          "nationalityID",
          "id",
          "_id",
          "key",
          "value",
          "code",
          "nationality_code",
          "nationalityCode",
        ]) ||
        singleEntry?.[0] ||
        getFallbackObjectValue(item, ["nationality", "id", "key", "value", "code"], [
          "name",
          "label",
          "title",
          "text",
        ]) ||
        label ||
        `nationality-${index}`;

      return label && id ? { id: String(id), label } : null;
    })
    .filter(Boolean)
    .reduce((uniqueOptions, option) => {
      if (!uniqueOptions.some((item) => item.id === option.id)) {
        uniqueOptions.push(option);
      }

      return uniqueOptions;
    }, []);
};

const formatDateForApi = (dateValue) => {
  const dateParts = String(dateValue || "").split("-");

  if (dateParts.length !== 3) {
    return dateValue;
  }

  const [year, month, day] = dateParts;
  return `${day}-${month}-${year}`;
};

const getResponseMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.data?.message ||
  error?.message ||
  fallback;

const isSuccessResponse = (response) =>
  response?.result === true ||
  response?.result === 1 ||
  response?.result === "1";

const Account = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nationalities = useSelector(
    (state) => state.common?.configurationData?.nationalities || []
  );
  const configurationData = useSelector(selectConfigurationData);
  const { user_location, country_id } = useSelector((state) => state.auth || {});
  const authUi = getScreenConfig(configurationData, "signup");
  const dateOfBirthInputRef = useRef(null);
  const defaultPhoneCountry = useMemo(
    () =>
      getDefaultAuthPhoneCountry({
        countryId: country_id,
        userLocation: user_location,
      }),
    [country_id, user_location]
  );
  const [formState, setFormState] = useState(initialFormState);
  const [selectedCountry, setSelectedCountry] = useState(defaultPhoneCountry);
  const [submitting, setSubmitting] = useState(false);
  const [loadingGuest, setLoadingGuest] = useState(false);

  const nationalityOptions = useMemo(() => {
    return normalizeNationalityOptions(nationalities);
  }, [nationalities]);

  const referralCodeFromUrl = searchParams.get("code") || "";
  const maxDate = new Date().toISOString().split("T")[0];
  const authStyle = {
    "--auth-bg": authUi.backgroundColor || "#ffffff",
    "--auth-input-bg": authUi.inputBackgroundColor || "#ffffff",
    "--auth-button-bg": authUi.buttonColor || "#42489b",
    "--auth-button-text": authUi.buttonTextColor || "#ffffff",
    "--auth-link": authUi.linkColor || "#24ca74",
  };

  useEffect(() => {
    setSelectedCountry(defaultPhoneCountry);
  }, [defaultPhoneCountry]);

  useEffect(() => {
    if (!referralCodeFromUrl) {
      return;
    }

    setFormState((previousState) => {
      if (previousState.referral_code === referralCodeFromUrl) {
        return previousState;
      }

      return {
        ...previousState,
        referral_code: referralCodeFromUrl,
      };
    });
  }, [referralCodeFromUrl]);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;

    setFormState((previousState) => ({
      ...previousState,
      [name]: type === "checkbox" ? checked : name === "phone" ? value.replace(/\D/g, "") : value,
    }));
  };

  const handleCountryChange = (event) => {
    const country = countriesData.find(
      (item) => item.id === parseInt(event.target.value, 10)
    );

    setSelectedCountry(country || countriesData[0]);
  };

  const openDatePicker = () => {
    const input = dateOfBirthInputRef.current;

    if (!input) {
      return;
    }

    input.focus();
    if (typeof input.showPicker === "function") {
      input.showPicker();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    const requiredFields = [
      { key: "first_name", message: "First name is required." },
      { key: "last_name", message: "Last name is required." },
      { key: "phone", message: "Mobile number is required." },
      { key: "email", message: "Email is required." },
      { key: "dob", message: "Date of birth is required." },
      { key: "nationality_id", message: "Nationality is required." },
      { key: "password", message: "Password is required." },
      { key: "confirm_password", message: "Confirm password is required." },
    ];

    const missingField = requiredFields.find(
      ({ key }) => !String(formState[key] ?? "").trim()
    );

    if (missingField) {
      toast.warning(missingField.message);
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email.trim())) {
      toast.warning("Please enter a valid email address.");
      return;
    }

    if (isIndiaPhoneCountry(selectedCountry) && !/^[6-9]\d{9}$/.test(formState.phone)) {
      toast.warning("Please enter a valid India mobile number.");
      return;
    }

    if (
      formState.phone.length < selectedCountry.minNumPhone ||
      formState.phone.length > selectedCountry.maxNumPhone
    ) {
      toast.warning(
        `Phone number must be between ${selectedCountry.minNumPhone} and ${selectedCountry.maxNumPhone} digits for ${selectedCountry.name}.`
      );
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!passwordRegex.test(formState.password)) {
      toast.warning(
        "Please enter password use a combination of upper and lowercase letters, numbers, and special symbols"
      );
      return;
    }

    if (formState.password !== formState.confirm_password) {
      toast.warning("Password and confirm password must match.");
      return;
    }

    if (!formState.terms) {
      toast.warning("Please accept the terms and privacy policy.");
      return;
    }

    const randomCpfNumber = String(Math.floor(1000 + Math.random() * 9000));
    const payload = new FormData();
    payload.append("first_name", formState.first_name.trim());
    payload.append("last_name", formState.last_name.trim());
    payload.append("password", formState.password);
    payload.append("email", formState.email.trim());
    payload.append("dob", formatDateForApi(formState.dob));
    payload.append("nationality_id", formState.nationality_id);
    payload.append("country_id", formState.nationality_id);
    payload.append("phone", `${selectedCountry.phonecode}${formState.phone}`);
    payload.append("smoker_type", "1");
    payload.append("questions", "");
    payload.append("user_cpf_number", randomCpfNumber);
    payload.append("network_code", "");
    payload.append("referral_code", formState.referral_code.trim());
    payload.append("requested_from", "web");

    setSubmitting(true);

    dispatch(signUp(payload))
      .then((response) => {
        toast.success(response?.message || "Sign up successful.");
        setFormState(initialFormState);
        setSelectedCountry(defaultPhoneCountry);
        if (isSuccessResponse(response)) {
          navigate("/login");
        }
      })
      .catch((error) => {
        toast.warning(getResponseMessage(error, "Sign up failed."));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/login");
  };

  const handleGuestLogin = async () => {
    if (loadingGuest) return;

    setLoadingGuest(true);

    try {
      await performGuestLogin(dispatch);
      toast.success("Guest login successful. You can browse the site now.");
      navigate("/home");
    } catch (error) {
      clearGuestSession();
      toast.error(error?.message || "Guest login failed.");
    } finally {
      setLoadingGuest(false);
    }
  };

  return (
    <section className={styles["auth-section"]} style={authStyle}>
      <div className={styles["auth-mobile-top"]}>
        <button
          type="button"
          className={styles["mobile-back-button"]}
          onClick={handleBack}
          aria-label="Go back"
        >
          <i className="ph ph-arrow-left" />
        </button>
        <button
          type="button"
          className={styles["mobile-skip-button"]}
          onClick={handleGuestLogin}
          disabled={loadingGuest}
        >
          {loadingGuest ? "Please wait..." : "Login Skip"}
        </button>
      </div>

      <div className={`container ${styles["auth-container"]}`}>
        <div className={styles["auth-card"]}>
          {/* LEFT BANNER */}
          <div className={styles["auth-banner"]}>
            <div className={styles["auth-banner-text-box"]}>
              <h2>
                Freshness delivered <br /> to your door <br /> in minutes.
              </h2>
              <p>Hand-picked quality you can trust.</p>
            </div>

            <div className={styles["orange-cluster"]} aria-hidden="true" />

            <div className={styles["auth-banner-image-div"]}>
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/auth/fruits.png`}
                alt="Fresh fruits"
                className={styles["auth-fruits"]}
              />
            </div>
          </div>

          {/* RIGHT FORM */}
          <form className={styles["auth-form"]} onSubmit={handleSubmit} noValidate>
            <div className={styles["auth-form-header"]}>
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/logo/rghtnow-blue-logo.png`}
                alt="Rghtnow"
                className={styles["logo"]}
              />
              <h3>Register</h3>
            </div>

            <div className={styles["form-grid"]}>
              <div className={styles["form-field"]}>
                <input
                  type="text"
                  name="first_name"
                  value={formState.first_name}
                  onChange={handleChange}
                  className={styles["auth-input"]}
                  placeholder="First Name"
                  autoComplete="given-name"
                />
              </div>

              <div className={styles["form-field"]}>
                <input
                  type="text"
                  name="last_name"
                  value={formState.last_name}
                  onChange={handleChange}
                  className={styles["auth-input"]}
                  placeholder="Last Name"
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div
              className={`${styles["form-field"]} ${styles["country-phone-container"]}`}
            >
              <div className={styles["country-select-wrapper"]}>
                <select
                  name="country_code"
                  value={selectedCountry.id}
                  onChange={handleCountryChange}
                  className={styles["country-select"]}
                  aria-label="Country code"
                >
                  {countriesData.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.flagEmoji} {country.phonecode} {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles["phone-input-wrapper"]}>
                <input
                  type="tel"
                  name="phone"
                  value={formState.phone}
                  onChange={handleChange}
                  className={styles["auth-input"]}
                  placeholder="Enter Mobile Number"
                  autoComplete="tel"
                  maxLength={selectedCountry.maxNumPhone}
                />
              </div>
            </div>

            <div className={`${styles["form-field"]} ${styles["icon-field"]}`}>
              <i className="ph ph-envelope-simple" />
              <input
                type="email"
                name="email"
                value={formState.email}
                onChange={handleChange}
                className={styles["auth-input"]}
                placeholder="Enter Email"
                autoComplete="email"
              />
            </div>

            <div className={styles["form-grid"]}>
              <div className={`${styles["form-field"]} ${styles["date-field"]}`}>
                <input
                  type="date"
                  name="dob"
                  ref={dateOfBirthInputRef}
                  value={formState.dob}
                  onChange={handleChange}
                  className={`${styles["auth-input"]} ${styles["date-input"]}`}
                  placeholder="Date of Birth"
                  max={maxDate}
                  aria-label="Date of birth"
                />
                <button
                  type="button"
                  className={styles["date-picker-button"]}
                  onClick={openDatePicker}
                  aria-label="Open date picker"
                >
                  <i className="ph ph-calendar-blank" />
                </button>
              </div>

              <div className={styles["form-field"]}>
                <select
                  name="nationality_id"
                  value={formState.nationality_id}
                  onChange={handleChange}
                  className={styles["auth-select"]}
                >
                  <option value="">Nationality</option>
                  {nationalityOptions.map((nationality) => (
                    <option key={nationality.id} value={nationality.id}>
                      {nationality.label}
                    </option>
                  ))}
                </select>
                <i className={`ph ph-caret-down ${styles["select-arrow"]}`} />
              </div>
            </div>

            <div className={`${styles["form-field"]} ${styles["icon-field"]}`}>
              <i className="ph ph-lock-key" />
              <input
                type="password"
                name="password"
                value={formState.password}
                onChange={handleChange}
                className={styles["auth-input"]}
                placeholder="Password"
                autoComplete="new-password"
              />
            </div>

            <div className={`${styles["form-field"]} ${styles["icon-field"]}`}>
              <i className="ph ph-lock-key" />
              <input
                type="password"
                name="confirm_password"
                value={formState.confirm_password}
                onChange={handleChange}
                className={styles["auth-input"]}
                placeholder="Confirm Password"
                autoComplete="new-password"
              />
            </div>

            <div className={styles["form-field"]}>
              <input
                type="text"
                name="referral_code"
                value={formState.referral_code}
                onChange={handleChange}
                className={styles["auth-input"]}
                placeholder="Enter Referral Code (Optional)"
              />
            </div>

            <label className={styles["terms-row"]}>
              <input
                type="checkbox"
                name="terms"
                checked={formState.terms}
                onChange={handleChange}
              />
              <span>
                By continuing, you agree to our{" "}
                <Link to="#">Term of Service</Link> and{" "}
                <Link to="#">Privacy Policy</Link>.
              </span>
            </label>

            <button
              type="submit"
              className={styles["btn-signup"]}
              disabled={submitting}
            >
              {submitting ? "Processing..." : "Sign Up"}
            </button>

            <p className={styles["signup-text"]}>
              Already have an account?{" "}
              <Link to="/login">
                <span>Login</span>
              </Link>
            </p>
          </form>

          <img
            src={
              authUi.footerImage ||
              `${process.env.PUBLIC_URL}/assets/images/auth/generated/auth-fruit-footer-crop.png`
            }
            alt=""
            className={styles["auth-mobile-fruits"]}
          />
        </div>
      </div>
    </section>
  );
};

export default Account;
